import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import styles from "../CRMSettingsPage.module.css";

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function safeFileNameFromUrl(url) {
  if (!url) return "";
  try {
    const u = new URL(url);
    const name = u.pathname.split("/").pop();
    return name || url;
  } catch {
    return String(url);
  }
}

function FileUploadRow({
  title,
  hint,
  accept,
  currentUrl,
  previewUrl,
  onPick,
  onRemove,
  t,
}) {
  const id = useMemo(() => `file_${Math.random().toString(16).slice(2)}`, []);
  const shownName = previewUrl
    ? t("crm_settings_selected_file_uploaded", {
        defaultValue: "Selected file uploaded",
      })
    : currentUrl
    ? safeFileNameFromUrl(currentUrl)
    : t("crm_settings_no_file", { defaultValue: "No file" });

  return (
    <div className={styles.assetBlock}>
      <div className={styles.assetHead}>
        <div className={styles.assetTitle}>{title}</div>
        {hint ? <div className={styles.assetHint}>{hint}</div> : null}
      </div>

      <div className={styles.assetRowCompact}>
        <input
          id={id}
          type="file"
          accept={accept}
          onChange={onPick}
          className={styles.hiddenFile}
        />

        <label htmlFor={id} className={styles.chooseBtn}>
          <span className={styles.chooseIcon}>⬆️</span>
          {t("crm_settings_upload", { defaultValue: "Upload" })}
        </label>

        <div className={styles.meta} title={currentUrl || ""}>
          {shownName}
        </div>

        <div className={styles.assetActions}>
          <button
            type="button"
            className={styles.ghostBtn}
            disabled={!currentUrl}
            onClick={async () => {
              if (!currentUrl) return;
              const ok = await copyToClipboard(currentUrl);
              if (!ok)
                alert(
                  t("crm_settings_copy_failed", {
                    defaultValue: "Failed to copy link",
                  })
                );
            }}
          >
            {t("crm_settings_copy_link", { defaultValue: "Copy link" })}
          </button>

          <button
            type="button"
            className={styles.dangerBtn}
            disabled={!currentUrl}
            onClick={currentUrl ? onRemove : undefined}
          >
            {t("crm_settings_remove", { defaultValue: "Remove" })}
          </button>
        </div>
      </div>
    </div>
  );
}

function BrowserLoginPreview({
  companyName,
  logoSrc,
  faviconSrc,
  bgColor,
  btnColor,
  t,
}) {
  return (
    <div className={styles.browserPreview}>
      <div className={styles.browserTop}>
        <div className={styles.browserTabs}>
          <div className={styles.browserTabActive}>
            {faviconSrc ? (
              <img
                className={styles.browserFaviconTab}
                src={faviconSrc}
                alt={t("crm_settings_browser_favicon_alt", {
                  defaultValue: "Favicon",
                })}
              />
            ) : (
              <span className={styles.browserFaviconFallbackTab}>🌐</span>
            )}
            <span className={styles.browserTabTitle}>
              {companyName || t("login", { defaultValue: "Login" })}
            </span>
            <span className={styles.browserTabClose}><i className="lni lni-xmark"></i></span>
          </div>
          <div className={styles.browserTabGhost}><i className="lni lni-plus me-1" /></div>
        </div>

        <div className={styles.browserControls}>
          <span className={styles.browserDot} />
          <span className={styles.browserDot} />
          <span className={styles.browserDot} />
        </div>
      </div>

      <div className={styles.browserPage} style={{ background: bgColor }}>
        <div className={styles.siteCanvas}>
          <div className={styles.bgLogoRow}>
            <img
              className={styles.bgLogo}
              src={logoSrc}
              alt={t("crm_settings_browser_bg_logo_alt", {
                defaultValue: "Logo",
              })}
            />
          </div>

          <div className={styles.browserLoginCard}>
            <div className={styles.browserLogoRow}>
              <img
                className={styles.browserLogo}
                src={logoSrc}
                alt={t("crm_settings_browser_logo_alt", {
                  defaultValue: "Logo",
                })}
              />
            </div>

            <div className={styles.browserTitle}>
              {t("login", { defaultValue: "Login" })}
            </div>

            <div className={styles.browserField}>
              <div className={styles.browserLabel}>
                {t("username", { defaultValue: "Username" })}
              </div>
              <div className={styles.browserInput}>
                {t("crm_settings_preview_email_placeholder", {
                  defaultValue: "youremail@shouldbe.here",
                })}
              </div>
            </div>

            <div className={styles.browserField}>
              <div className={styles.browserLabel}>
                {t("password", { defaultValue: "Password" })}
              </div>
              <div className={styles.browserInput}>••••••••</div>
            </div>

            <button
              className={styles.browserLoginBtn}
              style={{ background: btnColor }}
            >
              {t("login", { defaultValue: "Login" })}
            </button>

            <div className={styles.browserHint}>
              {t("crm_settings_live_preview_hint", {
                defaultValue:
                  "Live preview: logo / favicon / colors update immediately.",
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BrandingTab({
  companyName,
  setCompanyName,
  primaryColor,
  setPrimaryColor,
  loginButtonColor,
  setLoginButtonColor,
  allowPublicEmployeeRegistration,
  setAllowPublicEmployeeRegistration,
  verificationCodeTtlMinutes,
  setVerificationCodeTtlMinutes,
  logoUrl,
  previewLogoUrl,
  faviconUrl,
  previewFaviconUrl,
  onPickLogo,
  removeLogo,
  onPickFavicon,
  removeFavicon,
  logoForBrowser,
  faviconForBrowser,
}) {
  const { t } = useTranslation();

  return (
    <div className={styles.grid2x2}>
      <div className={`${styles.card} ${styles.brandingCard}`}>
        <div className={styles.cardHead}>
          <div>
            <div className={styles.cardTitle}>
              {t("crm_settings_branding_title", {
                defaultValue: "Branding",
              })}
            </div>
            <div className={styles.cardSub}>
              {t("crm_settings_branding_subtitle", {
                defaultValue: "Logo, name and primary color used across CRM.",
              })}
            </div>
          </div>
        </div>

        <div className={styles.formGrid}>
          <div className={styles.formLabel}>
            {t("crm_settings_company_name_label", {
              defaultValue: "Company name",
            })}
          </div>

          <input
            className={styles.textInput}
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder={t("crm_settings_company_name_placeholder", {
              defaultValue: "Company name",
            })}
          />

          <div className={styles.formLabel}>
            {t("crm_settings_primary_color_login_bg", {
              defaultValue: "Primary color (Login background)",
            })}
          </div>
          <div className={styles.colorRow}>
            <input
              className={styles.colorInput}
              type="color"
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
            />
            <input
              className={styles.textInput}
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
            />
          </div>

          <div className={styles.formLabel}>
            {t("crm_settings_login_button_color", {
              defaultValue: "Login button color",
            })}
          </div>
          <div className={styles.colorRow}>
            <input
              className={styles.colorInput}
              type="color"
              value={loginButtonColor}
              onChange={(e) => setLoginButtonColor(e.target.value)}
            />
            <input
              className={styles.textInput}
              value={loginButtonColor}
              onChange={(e) => setLoginButtonColor(e.target.value)}
            />
          </div>

          <div className={styles.formLabel}>
            {t("crm_settings_public_registration_enabled_hint", {
              defaultValue: "Show /employee-register publicly",
            })}
          </div>
          <div className={styles.inlineToggleRow}>
            <label className={styles.inlineToggle}>
              <input
                type="checkbox"
                checked={allowPublicEmployeeRegistration}
                onChange={(e) =>
                  setAllowPublicEmployeeRegistration(e.target.checked)
                }
              />
              <span className={styles.inlineToggleText}>
                {allowPublicEmployeeRegistration
                  ? t("crm_settings_on", { defaultValue: "On" })
                  : t("crm_settings_off", { defaultValue: "Off" })}
              </span>
            </label>
          </div>

          {allowPublicEmployeeRegistration && (
            <>
              <div className={styles.formLabel}>
                {t("crm_settings_registration_code_ttl_label", {
                  defaultValue: "Verification code lifetime",
                })}
              </div>

              <div className={styles.publicRegisterSettingsBox}>
                <div className={styles.publicRegisterSettingsHeader}>
                  <div className={styles.publicRegisterSettingsTitle}>
                    {t("crm_settings_public_registration_settings_title", {
                      defaultValue: "Public employee registration",
                    })}
                  </div>
                  <div className={styles.publicRegisterSettingsHint}>
                    {t("crm_settings_public_registration_settings_hint", {
                      defaultValue:
                        "These settings apply to the public /employee-register page.",
                    })}
                  </div>
                </div>

                <div className={styles.publicRegisterSettingsRow}>
                  <input
                    className={styles.textInput}
                    type="number"
                    min="1"
                    max="60"
                    value={verificationCodeTtlMinutes}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const num = Number(raw);

                      if (!Number.isFinite(num)) {
                        setVerificationCodeTtlMinutes(3);
                        return;
                      }

                      setVerificationCodeTtlMinutes(
                        Math.max(1, Math.min(60, num))
                      );
                    }}
                    placeholder="3"
                  />

                  <div className={styles.publicRegisterSettingsSuffix}>
                    {t("landing_minutes_short", {
                      defaultValue: "min",
                    })}
                  </div>
                </div>

                <div className={styles.publicRegisterSettingsHelp}>
                  {t("crm_settings_registration_code_ttl_help", {
                    defaultValue:
                      "Defines how long the email verification code remains valid.",
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className={styles.previewSpan}>
        <BrowserLoginPreview
          companyName={companyName}
          logoSrc={logoForBrowser}
          faviconSrc={faviconForBrowser}
          bgColor={primaryColor}
          btnColor={loginButtonColor || primaryColor}
          t={t}
        />
      </div>

      <div className={`${styles.card} ${styles.assetsCard}`}>
        <div className={styles.cardHeadRow}>
          <div className={styles.cardTitle}>
            {t("crm_settings_assets", { defaultValue: "Assets" })}
          </div>
          <div className={styles.smallMuted}>
            {t("crm_settings_assets_hint", {
              defaultValue: "Upload / Copy link / Remove",
            })}
          </div>
        </div>

        <div className={styles.assetsWrap}>
          <FileUploadRow
            title={t("crm_settings_logo_title", {
              defaultValue: "CRM Logo",
            })}
            hint={t("crm_settings_logo_hint", {
              defaultValue: "Recommended: PNG/SVG",
            })}
            accept="image/*"
            currentUrl={logoUrl}
            previewUrl={previewLogoUrl}
            onPick={onPickLogo}
            onRemove={removeLogo}
            t={t}
          />

          <div className={styles.divider} />

          <FileUploadRow
            title={t("crm_settings_favicon_title", {
              defaultValue: "Favicon Icon",
            })}
            hint={t("crm_settings_favicon_hint", {
              defaultValue: "Recommended: .ico / 32x32",
            })}
            accept="image/*,.ico"
            currentUrl={faviconUrl}
            previewUrl={previewFaviconUrl}
            onPick={onPickFavicon}
            onRemove={removeFavicon}
            t={t}
          />
        </div>
      </div>
    </div>
  );
}